import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from './api';
import { KEYS, storage } from './storage';

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('loading');

  const bootstrap = useCallback(async () => {
    const at = storage.get(KEYS.accessToken);
    if (!at) { setStatus('signed-out'); return; }
    try {
      const me = await apiFetch('/api/v1/auth/me');
      setUser(me.user);
      setStatus('signed-in');
    } catch {
      storage.remove(KEYS.accessToken);
      storage.remove(KEYS.refreshToken);
      setUser(null);
      setStatus('signed-out');
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const signIn = useCallback(async (email, password, device_name) => {
    const body = await apiFetch('/api/v1/auth/sign-in', {
      method: 'POST', auth: false,
      body: JSON.stringify({ email, password, device_name }),
    });
    storage.set(KEYS.accessToken, body.access_token);
    storage.set(KEYS.refreshToken, body.refresh_token);
    setUser(body.user);
    setStatus('signed-in');
  }, []);

  const applyTokenPair = useCallback((access, refresh, u) => {
    storage.set(KEYS.accessToken, access);
    storage.set(KEYS.refreshToken, refresh);
    setUser(u);
    setStatus('signed-in');
  }, []);

  const signOut = useCallback(async (all = false) => {
    try {
      await apiFetch(all ? '/api/v1/auth/sign-out-all' : '/api/v1/auth/sign-out', {
        method: 'POST', skipRefresh: true,
      });
    } catch { /* even if network fails, drop locally */ }
    storage.remove(KEYS.accessToken);
    storage.remove(KEYS.refreshToken);
    setUser(null);
    setStatus('signed-out');
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await apiFetch('/api/v1/auth/me');
      setUser(me.user);
    } catch { /* ignore transient */ }
  }, []);

  const value = useMemo(
    () => ({ status, user, signIn, applyTokenPair, signOut, refreshUser }),
    [status, user, signIn, applyTokenPair, signOut, refreshUser],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth must be inside AuthProvider');
  return c;
}
